// Theme management — 7-slot tiered theme system
// Free: light + bluer-slate | Starter: + soft/stone/plum/midnight | Pro: + custom builder
// CSS var values live in src/index.css — this module only manages selection + custom theme.

/**
 * @typedef {'free' | 'starter' | 'pro' | 'enterprise'} PlanTier
 */

/**
 * @typedef {Record<string, string>} ThemeVars
 * CSS custom property map (e.g. { '--color-bg': '#1f2230' }).
 */

/**
 * @typedef {Object} ThemeDef
 * @property {string} label
 * @property {'light' | 'dark' | 'custom'} group
 * @property {PlanTier} tier
 * @property {boolean | null} darkClass  true=dark, false=light, null=custom (derived)
 */

// Legacy CSS var names (pre-handoff-adoption) → new names. Used on a saved custom theme
// the first time we see it after the token rename.
/** @type {Record<string, string>} */
const LEGACY_VAR_MIGRATION = {
  '--bg-page': '--color-bg',
  '--bg-surface': '--color-card',
  '--bg-elevated': '--color-card-alt',
  '--bg-sidebar': '--color-sidebar',
  '--bg-input': '--color-card-alt',
  '--border-default': '--color-border',
  '--border-subtle': '--color-border-subtle',
  '--text-primary': '--color-text',
  '--text-secondary': '--color-text-dim',
  '--text-tertiary': '--color-text-muted',
  '--text-muted': '--color-text-faint',
}

// Legacy theme keys (old dark themes) → new replacement.
// `deep` had no direct equivalent; `bluer-slate` is the least-radical swap.
// `midnight` key survives but now points to the new emerald-accent midnight.
/** @type {Record<string, string>} */
const LEGACY_THEME_MIGRATION = {
  dark: 'bluer-slate',
  deep: 'bluer-slate',
}

/** @type {Record<string, ThemeDef>} */
export const THEMES = {
  light:         { label: 'Light',       group: 'light',  tier: 'free',    darkClass: false },
  soft:          { label: 'Soft Gray',   group: 'light',  tier: 'starter', darkClass: false },
  stone:         { label: 'Stone',       group: 'light',  tier: 'starter', darkClass: false },
  'bluer-slate': { label: 'Bluer Slate', group: 'dark',   tier: 'free',    darkClass: true  },
  plum:          { label: 'Plum',        group: 'dark',   tier: 'starter', darkClass: true  },
  midnight:      { label: 'Midnight',    group: 'dark',   tier: 'starter', darkClass: true  },
  custom:        { label: 'Custom',      group: 'custom', tier: 'pro',     darkClass: null  },
}

/** @type {string[]} */
export const THEME_ORDER = ['light', 'soft', 'stone', 'bluer-slate', 'plum', 'midnight', 'custom']

/** @type {Record<PlanTier, number>} */
export const PLAN_ORDER = { free: 0, starter: 1, pro: 2, enterprise: 3 }

/**
 * Preview swatches for ThemePicker tiles. CSS vars on :root/[data-theme] selectors
 * aren't readable cross-theme, so we keep a small hex palette here purely for the tile UI.
 * @type {Record<string, { bg: string, sidebar: string, card: string, border: string, text: string, textDim: string, accent: string }>}
 */
export const THEME_PREVIEW = {
  light:         { bg: '#f3f5f8', sidebar: '#ffffff', card: '#ffffff', border: '#dee2e8', text: '#111827', textDim: '#6b7280', accent: '#16a34a' },
  soft:          { bg: '#f0f1f3', sidebar: '#e8e9ec', card: '#f8f8fa', border: '#d5d7db', text: '#1a1d24', textDim: '#5a5f6b', accent: '#16a34a' },
  stone:         { bg: '#e8e5e0', sidebar: '#d8d4ce', card: '#f2f0ec', border: '#c8c4bc', text: '#2c2824', textDim: '#6b6560', accent: '#16a34a' },
  'bluer-slate': { bg: '#1f2230', sidebar: '#242838', card: '#2a2f42', border: '#3c4460', text: '#ecedf4', textDim: '#a5abc4', accent: '#6fa8ff' },
  plum:          { bg: '#231d28', sidebar: '#29232f', card: '#322a39', border: '#463c50', text: '#f0ebf2', textDim: '#b6a7bd', accent: '#d77fd1' },
  midnight:      { bg: '#10121c', sidebar: '#151824', card: '#1d2133', border: '#2f3552', text: '#edeef5', textDim: '#9da4c2', accent: '#4ed9a3' },
}

/**
 * Default custom theme vars — starting point for Pro tenants opening the builder.
 * Matches the bluer-slate hex fallback so a brand-new custom theme looks sensible.
 * @type {ThemeVars}
 */
export const DEFAULT_CUSTOM_VARS = {
  '--color-bg': '#1f2230',
  '--color-sidebar': '#242838',
  '--color-card': '#2a2f42',
  '--color-card-alt': '#30374d',
  '--color-border': '#3c4460',
  '--color-border-strong': '#4c5678',
  '--color-border-subtle': '#353a52',
  '--color-text': '#ecedf4',
  '--color-text-dim': '#a5abc4',
  '--color-text-muted': '#7b83a0',
  '--color-text-faint': '#555b74',
  '--color-logo-bg': '#353d5f',
  '--color-accent': '#6fa8ff',
  '--color-accent-solid': '#5688d1',
  '--color-accent-hover': '#7ba4e6',
  '--color-accent-bg': 'rgba(86, 136, 209, 0.18)',
  '--color-accent-bg-strong': 'rgba(86, 136, 209, 0.32)',
  '--color-accent-border': 'rgba(86, 136, 209, 0.40)',
}

/**
 * Get the currently selected theme key.
 * One-shot migrates legacy keys `dark` / `deep` → `bluer-slate`.
 * @returns {string}
 */
export function getTheme() {
  const raw = localStorage.crewos_theme || 'light'
  const migrated = /** @type {Record<string, string>} */ (LEGACY_THEME_MIGRATION)[raw]
  if (migrated) {
    localStorage.crewos_theme = migrated
    return migrated
  }
  return raw
}

/**
 * Persist and apply a theme by name.
 * @param {string} themeName
 * @returns {void}
 */
export function setTheme(themeName) {
  localStorage.crewos_theme = themeName
  applyTheme()
}

/**
 * Migrate a saved custom theme vars object from legacy `--bg-*` names to
 * new `--color-*` names. Idempotent — a no-op if already migrated.
 * @param {Record<string, string>} vars
 * @returns {Record<string, string>}
 */
function migrateLegacyVars(vars) {
  /** @type {Record<string, string>} */
  const out = {}
  let changed = false
  for (const [k, v] of Object.entries(vars)) {
    const newKey = /** @type {Record<string, string>} */ (LEGACY_VAR_MIGRATION)[k]
    if (newKey) {
      out[newKey] = v
      changed = true
    } else {
      out[k] = v
    }
  }
  if (changed) {
    try { localStorage.crewos_custom_theme = JSON.stringify(out) } catch { /* quota / JSON guard */ }
  }
  return out
}

/**
 * Apply the currently stored theme to the document root:
 *  - sets `data-theme` attribute (drives CSS `[data-theme=X]` blocks)
 *  - toggles `.dark` class (drives shared dark semantic ramp + scrollbars/shadows)
 *  - toggles `.themed` class (drives legacy Tailwind-class overrides until Phase 1)
 *  - for custom theme: sets CSS vars inline from localStorage
 * @returns {void}
 */
export function applyTheme() {
  const themeName = getTheme()
  const theme = THEMES[themeName]
  if (!theme) return

  const root = document.documentElement

  // Wipe any inline --color-*/legacy --bg-* vars from a prior custom-theme application.
  clearInlineVars(root)

  if (themeName === 'custom') {
    /** @type {Record<string, string>} */
    let vars = {}
    try {
      const raw = localStorage.crewos_custom_theme
      if (raw) vars = migrateLegacyVars(JSON.parse(raw))
    } catch { vars = {} }
    Object.entries(vars).forEach(([k, v]) => { if (v) root.style.setProperty(k, v) })
    const dark = isVarsDark(vars)
    root.classList.toggle('dark', dark)
    root.classList.add('themed')
    root.setAttribute('data-theme', 'custom')
    return
  }

  // Named theme — data-theme drives the CSS block; .dark + .themed are behavioral hooks.
  const isDark = theme.darkClass === true
  root.classList.toggle('dark', isDark)

  if (themeName === 'light') {
    root.classList.remove('themed')
    root.removeAttribute('data-theme')
  } else {
    root.classList.add('themed')
    root.setAttribute('data-theme', themeName)
  }
}

/**
 * Remove any inline CSS custom property prefixed with --color-/--bg-/--text-/--border-.
 * Called before applying a new theme so the previous custom theme's inline vars don't bleed.
 * @param {HTMLElement} root
 */
function clearInlineVars(root) {
  const props = Array.from(root.style)
  for (const p of props) {
    if (p.startsWith('--color-') || p.startsWith('--bg-') || p.startsWith('--text-') || p.startsWith('--border-')) {
      root.style.removeProperty(p)
    }
  }
}

/**
 * Persist and apply a custom theme vars set.
 * @param {ThemeVars} vars
 * @returns {void}
 */
export function saveCustomTheme(vars) {
  localStorage.crewos_custom_theme = JSON.stringify(vars)
  localStorage.crewos_theme = 'custom'
  applyTheme()
}

/**
 * Retrieve the saved custom theme vars, or null if none saved.
 * Auto-migrates legacy var names on read.
 * @returns {ThemeVars | null}
 */
export function getCustomTheme() {
  try {
    const raw = localStorage.crewos_custom_theme
    if (!raw) return null
    return migrateLegacyVars(JSON.parse(raw))
  } catch { return null }
}

export function isDark() {
  return document.documentElement.classList.contains('dark')
}

/**
 * Whether a vars set is "dark" based on --color-bg luminance.
 * Only reads hex — custom builder always writes hex for neutrals.
 * @param {ThemeVars} vars
 * @returns {boolean}
 */
function isVarsDark(vars) {
  const bg = vars?.['--color-bg'] || '#ffffff'
  if (!bg.startsWith('#') || bg.length < 7) return false
  const r = parseInt(bg.slice(1, 3), 16)
  const g = parseInt(bg.slice(3, 5), 16)
  const b = parseInt(bg.slice(5, 7), 16)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return luminance < 0.5
}

/**
 * Derive all 6 accent tokens from a single hex color (for custom builder).
 *   solid   = as-picked
 *   hover   = +10% lightness toward white
 *   accent  = +25% lightness toward white (used for bright/link/active-nav text)
 *   bg      = rgba @ 0.18 alpha
 *   bg-strong = rgba @ 0.32 alpha
 *   border  = rgba @ 0.40 alpha
 * @param {string} hex   #rrggbb
 * @returns {Record<string, string>}
 */
export function deriveAccentTokens(hex) {
  const { r, g, b } = hexToRgb(hex)
  const hover = rgbToHex(shiftLightness(r, g, b, 10))
  const bright = rgbToHex(shiftLightness(r, g, b, 25))
  return {
    '--color-accent-solid':     hex,
    '--color-accent-hover':     hover,
    '--color-accent':           bright,
    '--color-accent-bg':        `rgba(${r}, ${g}, ${b}, 0.18)`,
    '--color-accent-bg-strong': `rgba(${r}, ${g}, ${b}, 0.32)`,
    '--color-accent-border':    `rgba(${r}, ${g}, ${b}, 0.40)`,
  }
}

/**
 * @param {string} hex
 * @returns {{ r: number, g: number, b: number }}
 */
function hexToRgb(hex) {
  const clean = hex.replace('#', '')
  return {
    r: parseInt(clean.slice(0, 2), 16) || 0,
    g: parseInt(clean.slice(2, 4), 16) || 0,
    b: parseInt(clean.slice(4, 6), 16) || 0,
  }
}

/**
 * @param {{ r: number, g: number, b: number }} rgb
 * @returns {string}
 */
function rgbToHex({ r, g, b }) {
  return '#' + [r, g, b].map(c => Math.max(0, Math.min(255, Math.round(c))).toString(16).padStart(2, '0')).join('')
}

/**
 * Lighten RGB toward white by a percentage (0-100).
 * @param {number} r
 * @param {number} g
 * @param {number} b
 * @param {number} percent
 * @returns {{ r: number, g: number, b: number }}
 */
function shiftLightness(r, g, b, percent) {
  const shift = (/** @type {number} */ c) => c + ((255 - c) * percent) / 100
  return { r: shift(r), g: shift(g), b: shift(b) }
}

/**
 * Whether a plan level can access a given theme.
 * @param {string} themeName
 * @param {PlanTier} plan
 * @returns {boolean}
 */
export function canUseTheme(themeName, plan) {
  const theme = THEMES[themeName]
  if (!theme) return false
  return PLAN_ORDER[plan] >= PLAN_ORDER[theme.tier]
}
